import { useState, useEffect } from 'react';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { useGetTradesListQuery, useGetTradesStatQuery } from '@/store/api';
import { useScheduledThrottle } from '@/hooks';
import { INITIAL_TRADE_STAT, TRADES_PAGE_SIZE } from '@/constants';

export function useEventTrades({
	eventId,
	isProbability,
	isActiveTabTrades,
}: {
	eventId: string | null;
	isProbability: boolean;
	isActiveTabTrades: boolean;
}) {
	const [selectedLobbyFilter, setSelectedLobbyFilter] = useState('all');
	const tradeFilterLobbyId =
		selectedLobbyFilter && selectedLobbyFilter !== 'all' ? selectedLobbyFilter : undefined;

	const [tradeStats, setTradeStats] = useState(INITIAL_TRADE_STAT);
	const [trades, setTrades] = useState<any[]>([]);
	const [tradePage, setTradePage] = useState(1);
	const [tradeTotal, setTradeTotal] = useState(0);
	const [hasLoadedTrades, setHasLoadedTrades] = useState(false);
	const [shouldRefetchTrade, setShouldRefetchTrade] = useState(false);

	useEffect(() => {
		if (isActiveTabTrades && !hasLoadedTrades) {
			setHasLoadedTrades(true);
		}
	}, [isActiveTabTrades, hasLoadedTrades]);

	const {
		data: tradesStatData,
		refetch: refetchTradesStat,
		isLoading: isLoadingTradeStat,
	} = useGetTradesStatQuery(
		eventId && hasLoadedTrades && !isProbability
			? { eventId, lobbyId: tradeFilterLobbyId }
			: skipToken,
		{
			refetchOnMountOrArgChange: true,
		}
	);

	const {
		data: tradeList,
		refetch: refetchTradeList,
		isLoading: isLoadingTradeList,
	} = useGetTradesListQuery(
		eventId && hasLoadedTrades && !isProbability
			? { eventId, lobbyId: tradeFilterLobbyId, pageSize: TRADES_PAGE_SIZE, page: tradePage }
			: skipToken,
		{
			refetchOnMountOrArgChange: true,
		}
	);
	const isLoadingTrades = isLoadingTradeList || isLoadingTradeStat;

	const { schedule: scheduleRefetchTradeStat, setRefetchFn: setTradeStatRefetch } =
		useScheduledThrottle(2000);
	const { schedule: scheduleRefetchTradeList, setRefetchFn: setTradeListRefetch } =
		useScheduledThrottle(2000);

	useEffect(() => {
		setTradeStatRefetch(refetchTradesStat);
	}, [refetchTradesStat, setTradeStatRefetch]);

	useEffect(() => {
		setTradeListRefetch(refetchTradeList);
	}, [refetchTradeList, setTradeListRefetch]);

	useEffect(() => {
		if (isActiveTabTrades && shouldRefetchTrade && hasLoadedTrades) {
			refetchTradesStat();
			refetchTradeList();
			setShouldRefetchTrade(false);
		}
	}, [isActiveTabTrades, shouldRefetchTrade, setShouldRefetchTrade]);

	useEffect(() => {
		if (tradesStatData) {
			setTradeStats({
				total: tradesStatData.total,
				settled: tradesStatData.total - tradesStatData.pending,
				pending: tradesStatData.pending,
			});
		}
	}, [tradesStatData]);

	useEffect(() => {
		if (tradeList) {
			setTrades(tradeList.list);
			setTradeTotal(tradeList.total);
		}
	}, [tradeList?.list]);

	useEffect(() => {
		setTradePage(1);

		if (!isActiveTabTrades && hasLoadedTrades) {
			setShouldRefetchTrade(true);
		}
	}, [hasLoadedTrades, isActiveTabTrades, selectedLobbyFilter]);

	return {
		selectedLobbyFilter,
		setSelectedLobbyFilter,
		tradeFilterLobbyId,
		tradeStats,
		trades,
		tradePage,
		setTradePage,
		tradeTotal,
		hasLoadedTrades,
		refetchTradesStat,
		refetchTradeList,
		isLoadingTrades,
		scheduleRefetchTradeStat,
		scheduleRefetchTradeList,
		shouldRefetchTrade,
		setShouldRefetchTrade,
	};
}
